import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Bell, Check, Command, Menu, Search, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { relative } from "@/lib/format";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { ScrollArea } from "@/components/ui/scroll-area";
import { UserAvatar } from "@/components/common/UserAvatar";
import { cn } from "@/lib/utils";

const ROLE_LABEL = { admin: "Administrator", sales: "Sales Staff", telecaller: "Telecaller", accountant: "Accountant", staff: "Staff" };

export const Topbar = ({ onMenu, onPalette }) => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [openSearch, setOpenSearch] = useState(false);
  const [notes, setNotes] = useState({ items: [], unread: 0 });

  const loadNotes = async () => {
    try {
      const { data } = await api.get("/notifications");
      setNotes(data);
    } catch { /* ignore */ }
  };

  useEffect(() => {
    loadNotes();
    const t = setInterval(loadNotes, 45000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    const t = setTimeout(async () => {
      try {
        const { data } = await api.get("/search", { params: { q } });
        setResults(data.results || []);
        setOpenSearch(true);
      } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(t);
  }, [q]);

  const markAll = async () => {
    await api.post("/notifications/read", {});
    loadNotes();
  };

  return (
    <header className="sticky top-0 z-30 flex h-16 items-center gap-3 border-b border-border bg-background/85 px-4 backdrop-blur-md sm:px-6">
      <Button variant="ghost" size="icon" onClick={onMenu} className="lg:hidden" data-testid="menu-toggle">
        <Menu className="h-5 w-5" />
      </Button>

      <Popover open={openSearch && results.length > 0} onOpenChange={setOpenSearch}>
        <PopoverTrigger asChild>
          <div className="relative w-full max-w-md">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            {searching && <Loader2 className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 animate-spin text-muted-foreground" />}
            <Input
              data-testid="global-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search leads, invoices, products..."
              className="h-9 rounded-lg pl-9"
            />
          </div>
        </PopoverTrigger>
        <PopoverContent align="start" className="w-[min(28rem,92vw)] p-1.5">
          <ScrollArea className="max-h-80">
            {results.map((r, i) => (
              <button
                key={i}
                onClick={() => { navigate(r.link); setOpenSearch(false); setQ(""); }}
                className="flex w-full items-center gap-3 rounded-md px-2.5 py-2 text-left transition-colors hover:bg-accent"
              >
                <span className="rounded bg-primary-soft px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-primary">{r.type}</span>
                <span className="min-w-0 flex-1">
                  <span className="block truncate text-sm font-medium">{r.title}</span>
                  <span className="block truncate text-xs text-muted-foreground">{r.subtitle}</span>
                </span>
              </button>
            ))}
          </ScrollArea>
        </PopoverContent>
      </Popover>

      <div className="ml-auto flex items-center gap-2">
        <Button variant="outline" size="sm" onClick={onPalette} data-testid="palette-btn"
          className="hidden h-9 gap-2 text-muted-foreground md:inline-flex">
          <Command className="h-3.5 w-3.5" />
          <span className="text-xs">Quick actions</span>
          <kbd className="rounded border border-border bg-muted px-1.5 py-0.5 text-[10px] font-semibold">Ctrl K</kbd>
        </Button>
        <Popover>
          <PopoverTrigger asChild>
            <Button variant="ghost" size="icon" className="relative" data-testid="notification-bell">
              <Bell className="h-[18px] w-[18px]" />
              {notes.unread > 0 && (
                <span className="absolute right-1.5 top-1.5 grid h-4 min-w-4 place-items-center rounded-full bg-destructive px-1 text-[10px] font-bold text-destructive-foreground">
                  {notes.unread}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent align="end" className="w-[min(22rem,92vw)] p-0">
            <div className="flex items-center justify-between border-b border-border px-4 py-3">
              <p className="font-display text-sm font-semibold">Notifications</p>
              {notes.items.length > 0 && (
                <Button variant="ghost" size="sm" onClick={markAll} className="h-7 text-xs">
                  <Check className="mr-1 h-3 w-3" /> Mark all read
                </Button>
              )}
            </div>
            <ScrollArea className="max-h-96">
              {notes.items.length === 0 ? (
                <p className="px-4 py-10 text-center text-sm text-muted-foreground">You have no notifications yet.</p>
              ) : (
                notes.items.map((n) => (
                  <button
                    key={n.id}
                    onClick={() => { if (n.link) navigate(n.link); api.post("/notifications/read", { id: n.id }).then(loadNotes); }}
                    className={cn("flex w-full flex-col gap-0.5 border-b border-border/60 px-4 py-3 text-left transition-colors hover:bg-accent", !n.read && "bg-primary-soft/40")}
                  >
                    <span className="text-sm font-medium">{n.title}</span>
                    <span className="text-xs text-muted-foreground">{n.body}</span>
                    <span className="text-[11px] text-muted-foreground/80">{relative(n.created_at)}</span>
                  </button>
                ))
              )}
            </ScrollArea>
          </PopoverContent>
        </Popover>

        <button
          onClick={() => navigate("/app/profile")}
          className="flex items-center gap-2.5 rounded-lg border border-border bg-card py-1 pl-1 pr-3 transition-colors hover:border-primary/40"
          data-testid="topbar-profile"
        >
          <UserAvatar key={user?.email} name={user?.name} src={user?.avatar} size="sm" />
          <span className="hidden text-left sm:block">
            <span className="block text-xs font-semibold leading-tight">{user?.name}</span>
            <span className="block text-[11px] leading-tight text-muted-foreground">{ROLE_LABEL[user?.role] || user?.role}</span>
          </span>
        </button>
      </div>
    </header>
  );
};
